import React from "react";
import { useNavigate } from "react-router-dom";
import { User, Mail, ShieldCheck, CreditCard, LogOut, BadgeCheck } from "lucide-react";
import { PageHeader } from "@/components/shell/TopBar";
import { MembershipBadge } from "@/components/badges/Badges";
import { StateChip } from "@/components/common/IntegrationStatus";
import { IntelligenceCard } from "@/components/cards/Cards";
import { Toggle, usePref } from "@/components/common/Toggle";
import { useSession } from "@/context/SessionContext";
import { useLaunch } from "@/context/LaunchContext";
import { fmtDuration } from "@/lib/format";
import { useI18n } from "@/i18n";
import { ManageSubscriptionButton, hasManageableSubscription } from "@/components/billing/ManageSubscriptionButton";

function Row({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-4">
      <span className="flex items-center gap-3 text-sm text-muted-foreground">{icon}{label}</span>
      <span className="text-sm font-semibold text-foreground">{value}</span>
    </div>
  );
}

export default function Account() {
  const { d, lang } = useI18n();
  const { user, tier, logout } = useSession();
  const { me, plan, isTrial, planLabel, secondsRemaining, trialEndsMs } = useLaunch();
  const navigate = useNavigate();
  const [nLive, setNLive] = usePref("sdip.notif.live", true);
  const [nResult, setNResult] = usePref("sdip.notif.result", true);

  return (
    <div data-testid="account-page">
      <PageHeader title={d.nav.account} subtitle={lang === "tr" ? "Profil, üyelik ve hesap tercihleri." : "Profile, membership and account preferences."} />

      <div className="grid gap-6 lg:grid-cols-2">
        <IntelligenceCard title={lang === "tr" ? "Profil" : "Profile"}>
          <div className="mb-4 flex items-center gap-4">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-atlas-500/90 to-atlas-700 text-xl font-extrabold text-atlas-ink">{(user?.displayName || "S")[0]}</span>
            <div>
              <p className="text-base font-bold text-foreground">{user?.displayName || "SDIP"}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
            {(plan || tier) && <div className="ml-auto flex items-center gap-2"><MembershipBadge tier={(plan ?? tier) as any} />{isTrial && <span className="rounded-full border border-gold/25 bg-gold/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gold-300">{lang === "tr" ? "Deneme" : "Trial"}</span>}</div>}
          </div>
          <div className="space-y-2">
            <Row icon={<Mail className="h-4 w-4" />} label="Email" value={user?.email || "—"} />
            <Row icon={<BadgeCheck className="h-4 w-4" />} label={lang === "tr" ? "Durum" : "Status"} value={user?.preview ? (lang === "tr" ? "Önizleme" : "Preview") : (lang === "tr" ? "Aktif" : "Active")} />
            <Row icon={<ShieldCheck className="h-4 w-4" />} label={lang === "tr" ? "Üyelik" : "Membership"} value={planLabel(lang) || tier || <span className="text-sm font-semibold text-muted-foreground">{lang === "tr" ? "Giriş gerekli" : "Login required"}</span>} />
            {isTrial && trialEndsMs != null && (
              <Row icon={<BadgeCheck className="h-4 w-4" />} label={lang === "tr" ? "Deneme bitişi" : "Trial ends"} value={new Date(trialEndsMs).toLocaleString(lang === "tr" ? "tr-TR" : "en-GB", { dateStyle: "medium", timeStyle: "short" })} />
            )}
            {isTrial && secondsRemaining != null && (
              <Row icon={<BadgeCheck className="h-4 w-4" />} label={lang === "tr" ? "Kalan süre" : "Time remaining"} value={fmtDuration(secondsRemaining, lang)} />
            )}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            {lang === "tr" ? "Profil düzenleme, hesabınızla giriş yaptığınızda etkinleşir." : "Profile editing activates once you log in with your account."}
          </p>
        </IntelligenceCard>

        <div className="space-y-6">
          <IntelligenceCard title={lang === "tr" ? "Bildirim Tercihleri" : "Notification Preferences"}>
            <div className="space-y-2">
              <div className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] p-4">
                <span className="text-sm text-foreground/90">{lang === "tr" ? "Yeni canlı zekâ" : "New LIVE intelligence"}</span>
                <Toggle checked={nLive} onChange={setNLive} testid="pref-notif-live" />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] p-4">
                <span className="text-sm text-foreground/90">{lang === "tr" ? "Sonuç (WIN / LOSE)" : "Results (WIN / LOSE)"}</span>
                <Toggle checked={nResult} onChange={setNResult} testid="pref-notif-result" />
              </div>
            </div>
          </IntelligenceCard>

          <IntelligenceCard title={lang === "tr" ? "Faturalandırma & Oturum" : "Billing & Session"}>
            {hasManageableSubscription(me) && (
              <div className="mb-4 space-y-2" data-testid="account-subscription">
                <Row icon={<ShieldCheck className="h-4 w-4" />} label={lang === "tr" ? "Mevcut plan" : "Current plan"} value={<span data-testid="account-subscription-plan">{planLabel(lang) || plan || "—"}</span>} />
                <Row icon={<BadgeCheck className="h-4 w-4" />} label={lang === "tr" ? "Abonelik durumu" : "Subscription status"} value={<span className="text-atlas-300" data-testid="account-subscription-status">{lang === "tr" ? "Aktif" : "Active"}</span>} />
                <p className="pt-1 text-xs text-muted-foreground">{lang === "tr" ? "Ödeme yönteminizi, faturalarınızı ve abonelik iptalinizi Stripe üzerinden yönetebilirsiniz." : "Manage your payment method, invoices and cancellation through Stripe."}</p>
                <ManageSubscriptionButton lang={lang} />
              </div>
            )}
            <button onClick={() => navigate("/app/membership")} data-testid="account-billing" className="mb-2 flex w-full items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] p-4 text-sm text-foreground transition hover:border-atlas/30">
              <span className="flex items-center gap-3"><CreditCard className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Üyelik & faturalandırma" : "Membership & billing"}</span>
              <span className="text-muted-foreground">›</span>
            </button>
            <button onClick={() => { navigate("/"); logout(); }} data-testid="account-logout" className="flex w-full items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] p-4 text-sm text-foreground transition hover:border-live/30 hover:text-live-400">
              <span className="flex items-center gap-3"><LogOut className="h-4 w-4" /> {lang === "tr" ? "Çıkış yap" : "Log out"}</span>
            </button>
          </IntelligenceCard>
        </div>
      </div>
    </div>
  );
}
